import { NonNullish } from "./nullish-check";
import fetchText from "./fetch-text";
import Cart from "./cart";
import type { Product } from '@grafikr/shopify-typescript/type/liquid/Product';
import type { Recommendations } from '@grafikr/shopify-typescript/type/endpoints/Product';
import App from "@grafikr/components/lib/app";

interface SellingPlanGroup {
  app_id: string;
  id: string;
  name: string;
  options: {
    name: string;
    position: number;
    values: string[];
  }[];
  selling_plans: {
    id: number;
    description: string;
    name: string;
    recurring_deliveries: boolean;
  }[];
}

export default class CartDrawer {
  private readonly sectionId: string;
  private readonly drawerElement: HTMLElement;
  private app: App;

  constructor(element: HTMLElement, app: App) {
    this.drawerElement = element;
    this.sectionId = this.extractSectionId();
    this.app = app;

    document.addEventListener("cart:change", this.onCartChange.bind(this));
    document.addEventListener("cart:rerender", this.refreshCart.bind(this));
    window.addEventListener("pageshow", this.onPageShow.bind(this));

    this.addEventListeners();
    this.renderRecommendations();
  }

  async renderRecommendations() {
    const recommendationsElem = this.drawerElement.querySelector<HTMLElement>('[data-cart-recommendations]');
    const promises: Promise<Response>[] = [];

    if (!recommendationsElem) {
      return;
    }

    theme.cart.items.forEach((item: { product_id: { toString: () => any } }) => {
      const params = new URLSearchParams({
        product_id: item.product_id.toString(),
        intent: 'related',
        limit: '4',
      });

      promises.push(fetch(`${theme.routes.productRecommendations}.json?${params}`));
    });

    const responses = await Promise.all<Response>(promises);
    const bodies: Recommendations[] = await Promise.all(
      responses.map((response) => response.json()),
    );
    const allRecommendations = bodies.map((body) => body.products).flat() as unknown as Product[];
    const selectedRecommendations = allRecommendations
      .filter((product, index) => {
        const searchedIndex = allRecommendations.findIndex(
          (searchedProduct) => searchedProduct.id === product.id,
        );

        return searchedIndex === index;
      })
      .slice(0, 12);

    const productItems = selectedRecommendations.map((product) => fetch(`/products/${product.handle}?view=recommendation-item`).then((res) => res.text()));
    const productItemResponses = await Promise.all(productItems);

    recommendationsElem.innerHTML = productItemResponses.join('');
    NonNullish(recommendationsElem.parentElement).hidden = productItemResponses.length === 0;
    this.app.mount(this.drawerElement);
  }

  async checkSellingPlans(node: HTMLElement) {
    let sellingPlans;
    const data: {
      selling_plan_groups: SellingPlanGroup[];
      id: number;
      url: string
    } = JSON.parse(NonNullish(node.previousElementSibling).innerHTML);

    const sps =
      data.selling_plan_groups?.flatMap((spg) =>
        spg?.selling_plans.map((sp) => sp.id)
      ) || [];
    const HIDDEN_PLANS_STORAGE_KEY = `sps-${data.id}-${sps.join('-')}`;

    if (sessionStorage.getItem(HIDDEN_PLANS_STORAGE_KEY)) {
      sellingPlans = JSON.parse(sessionStorage.getItem(HIDDEN_PLANS_STORAGE_KEY)!);
    } else {
      const body = {
        domain: window.Shopify.shop,
        sellingPlanIds: sps,
        product_shopify_id: data.id,
      };

      const bundleRes = await fetch(
        'https://apiv2.loopwork.co/bundleStorefront/filterBundleSellingPlanIds',
        {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const bundleResJSON = await bundleRes.json();
      sellingPlans = bundleResJSON.data;

      sessionStorage.setItem(HIDDEN_PLANS_STORAGE_KEY, JSON.stringify(sellingPlans));
    }

    const spgs =
      data.selling_plan_groups?.flatMap((spg) =>
        spg.selling_plans.map((sp) => ({
          selling_plan_group_id: spg.id,
          selling_plan_id: sp.id,
        }))
      ) || [];

    for (const spg of spgs) {
      if (sellingPlans.includes(spg.selling_plan_id) || sellingPlans.includes(spg.selling_plan_id.toString())) {
        const elem = node.querySelector(
          `option[value="${spg.selling_plan_id}"]`
        );

        if (elem) elem.remove();
      }
    }
  }

  addEventListeners() {
    const lineItemQuantities = this.drawerElement.querySelectorAll('a[href*="/cart/change"]');
    const lineItemSubscriptions = this.drawerElement.querySelectorAll<HTMLElement>('[data-selling-plan-switch-wrapper]');

    lineItemQuantities.forEach((item) => {
      item.addEventListener('click', this.changeLineItemQuantity.bind(this))
    });

    lineItemSubscriptions.forEach((item) => {
      const select = NonNullish(item.querySelector<HTMLSelectElement>('select[data-line-selling-plan]'));
      const subscriptionSwitch = NonNullish(item.querySelector<HTMLInputElement>('input[data-selling-plan-switch]'));
      this.checkSellingPlans(select);

      select.addEventListener('change', () => this.subscriptionChange(item))
      subscriptionSwitch.addEventListener('change', () => this.subscriptionChange(item))
    });
  }

  async subscriptionChange(item: HTMLElement) {
    const line = NonNullish(item.closest('[data-line]'));
    const quantityInput = NonNullish(line.querySelector<HTMLInputElement>('input[name="updates[]"]'));
    const subscriptionSwitch = NonNullish(item.querySelector<HTMLInputElement>('input[data-selling-plan-switch]'));
    const select = NonNullish(item.querySelector<HTMLSelectElement>('select[data-line-selling-plan]'));

    subscriptionSwitch.disabled = true;

    const formData = new FormData();
    formData.set('id', NonNullish(item.dataset.key));
    formData.set('quantity', quantityInput.value);
    formData.set('sections', this.sectionId);
    formData.set('selling_plan', subscriptionSwitch.checked ? select.value : '');

    await Cart.change(formData);

    this.refreshCart();
  }

  extractSectionId() {
    const section = NonNullish(this.drawerElement.classList.contains("shopify-section") ? this.drawerElement : this.drawerElement.closest(".shopify-section"));
    return section.id.replace("shopify-section-", "");
  }

  replaceContent(content: string) {
    const elementsToUpdate = this.drawerElement.querySelectorAll<HTMLElement>('[data-update]');
    const domElement = new DOMParser().parseFromString(content, "text/html");
    const drawerSection = NonNullish(domElement.getElementById(`shopify-section-${this.sectionId}`));

    const updates: Record<string, Element> = {};

    [...NonNullish(drawerSection.querySelectorAll<HTMLElement>('[data-update]'))].forEach(elem => {
      const tmpElement = document.createElement("div");
      tmpElement.insertAdjacentHTML('beforeend', elem.outerHTML);
      updates[NonNullish(elem.dataset.update)] = NonNullish(tmpElement.firstElementChild);
    });

    elementsToUpdate.forEach((elem) => {
      const updateType: string = NonNullish(elem.dataset.update);

      if (updateType == 'cart-shipping-progress') {
        elem.setAttribute('class', NonNullish(updates[updateType]).classList.toString());
        elem.setAttribute('style', NonNullish(NonNullish(updates[updateType]).getAttribute('style')));
        elem.firstElementChild!.setAttribute('style', NonNullish(NonNullish(updates[updateType]).firstElementChild!.getAttribute('style')));
      } else {
        elem.replaceWith(NonNullish(updates[updateType]))
      }
    })

    this.renderRecommendations();
    this.addEventListeners();
    document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));
  }

  onCartChange(event: CustomEvent) {
    this.replaceContent(event.detail.cart.sections[this.sectionId]);
  }

  async refreshCart() {
    const text = await fetchText(`${Shopify.routes.root}?section_id=${this.sectionId}`);
    this.replaceContent(text);
  }

  onPageShow(event: PageTransitionEvent) {
    if (!event.persisted) {
      return;
    }

    this.refreshCart();
  }

  async changeLineItemQuantity(event: Event) {
    event.preventDefault();

    const target = event.target as HTMLAnchorElement;
    target.classList.add('opacity-50', 'pointer-events-none');
    const url = new URL(target.href);

    const formData = new FormData();
    formData.set('id', NonNullish(url.searchParams.get('id')));
    formData.set('quantity', NonNullish(url.searchParams.get('quantity')));
    formData.set('sections', this.sectionId);

    const { data } = await Cart.change(formData);

    if (data) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          cart: data
        }
      }));
    }
  }
}